// ===========================================================================
// ReHome — Állatfotók láthatósága (privát / publikus)
//
// Minden feltöltött kép a PRIVÁT tárolóba kerül; onnan a menhely teheti át
// publikusba. A láthatóság váltása fizikai mozgatás a két bucket között —
// az indoklás a 042_animal_photo_visibility.sql fejlécében olvasható.
//
// Egy kép "hivatkozása" (ref) ezért kétféle lehet, és a kettő ránézésre
// megkülönböztethető:
//
//   publikus : teljes URL   (https://…/rehome_public/animals/{id}/…)
//   privát   : tároló-útvonal (animals/{id}/…) — csak aláírt linken át nézhető
//
// A felület sehol sem tárol külön "privát?" jelölőt: a hivatkozás alakja
// önmagában megmondja. Egy jelölő elkerülhetetlenül szétcsúszna attól, hogy
// az objektum valójában melyik bucketben van.
// ===========================================================================

import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from './supabase';

export const PUBLIC_BUCKET = 'rehome_public';
export const PRIVATE_BUCKET = 'rehome_private';

/** Aláírt linkek élettartama. Rövid, mert a felület újratöltéskor újat kér. */
export const SIGNED_URL_TTL_SECONDS = 3600;

/**
 * Publikus-e a hivatkozás. Az `http(s)` a feltöltött publikus képeké, a
 * `data:` a demo módé (ott nincs is tároló), a `blob:` pedig a még el sem
 * mentett, helyi előnézeté.
 */
export const isPublicPhotoRef = (ref: string): boolean =>
  /^(https?:|data:|blob:)/.test(ref);

/**
 * A publikus URL-ből visszafejti a tároló-útvonalat. A Supabase publikus URL
 * alakja `…/object/public/{bucket}/{path}`; ha a minta nem illeszkedik (idegen
 * eredetű kép, pl. seed-elt Unsplash-link), `null` jön vissza — az ilyet nem
 * lehet privátba mozgatni, és a felület nem is ajánlja fel.
 */
export const publicUrlToStoragePath = (url: string): string | null => {
  const marker = `/object/public/${PUBLIC_BUCKET}/`;
  const at = url.indexOf(marker);
  if (at === -1) return null;
  const path = url.slice(at + marker.length).split('?')[0];
  return path ? decodeURIComponent(path) : null;
};

/** Egy állat összes fotója megjelenítési sorrendben: előbb a publikusak. */
export const allPhotoRefs = (animal: { gallery?: string[] | null; privateGallery?: string[] | null } | null | undefined): string[] => [
  ...(animal?.gallery || []),
  ...(animal?.privateGallery || []),
];

/**
 * Megjeleníthető URL-ek a hivatkozásokhoz, EGYETLEN kéréssel.
 *
 * A privát képekhez aláírt link kell. Ha ezt képenként kérnénk, egy 20 soros
 * állatlista 20 külön hálózati kört indítana — ezért a `createSignedUrls`
 * kötegelt hívása megy, hivatkozásonként nem. A publikus képek változatlanul
 * mennek át: azoknál a CDN dolgozik, aláírás nem kell.
 *
 * A visszatérő map csak a feloldható képeket tartalmazza; egy hiányzó kulcs
 * azt jelenti, hogy a képet nem lehet megmutatni (törölték, vagy nincs rá jog).
 */
export const resolvePhotoUrls = async (
  refs: string[],
  expiresIn: number = SIGNED_URL_TTL_SECONDS
): Promise<Record<string, string>> => {
  const out: Record<string, string> = {};
  const privatePaths: string[] = [];

  for (const ref of refs) {
    if (!ref) continue;
    if (isPublicPhotoRef(ref)) out[ref] = ref;
    else privatePaths.push(ref);
  }

  if (privatePaths.length === 0 || !isSupabaseConfigured()) return out;

  try {
    const { data, error } = await supabase.storage
      .from(PRIVATE_BUCKET)
      .createSignedUrls(privatePaths, expiresIn);

    if (error) throw error;
    for (const entry of data || []) {
      // A kötegelt válasz elemenként hibázhat — a hibás egyszerűen kimarad.
      if (entry.signedUrl && entry.path) out[entry.path] = entry.signedUrl;
    }
  } catch {
    // Csendes: a hívó a hiányzó kulcsból tudja, hogy nincs megjeleníthető kép.
  }

  return out;
};

// ═══════════════════════════════════════════════════════════════════════════
// Borítóképek a menhelyi felületen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Egy állat borítóképének hivatkozása a MENHELYI nézetben.
 *
 * A platform csak publikus képet mutathat, a menhely viszont a sajátjait
 * mind látja — ezért ha egy publikus kép sincs, a lista a legelső privát
 * képet mutatja. Így egy frissen bekerült állat sem marad kép nélkül a
 * menhely saját listájában, miközben a platformon (helyesen) nem látszik,
 * amíg nem publikálják.
 */
export const staffCoverRef = (animal: {
  imageUrl?: string | null;
  gallery?: string[] | null;
  privateGallery?: string[] | null;
} | null | undefined): string | null =>
  animal?.imageUrl || animal?.gallery?.[0] || animal?.privateGallery?.[0] || null;

/**
 * Megjeleníthető borítókép-URL-ek állat-azonosító szerint, EGY kötegelt
 * aláírás-kéréssel az egész listára. Húsz állat = egy kérés, nem húsz.
 */
export const useAnimalCoverUrls = (
  animals: Array<{ id: string; imageUrl?: string | null; gallery?: string[] | null; privateGallery?: string[] | null }> | null | undefined
): Record<string, string> => {
  const [urls, setUrls] = useState<Record<string, string>>({});

  // A hatás bemenete a hivatkozások listája, nem a tömb azonossága: egy
  // újrarenderelés önmagában ne indítson új aláírás-kérést.
  const pairs = (animals || [])
    .map(a => [a.id, staffCoverRef(a)] as const)
    .filter((p): p is readonly [string, string] => Boolean(p[1]));
  const key = pairs.map(([id, ref]) => `${id}\u0000${ref}`).join('|');

  useEffect(() => {
    let cancelled = false;
    if (!key) { setUrls({}); return; }

    const entries = key.split('|').map(part => part.split('\u0000') as [string, string]);
    resolvePhotoUrls(entries.map(([, ref]) => ref)).then(resolved => {
      if (cancelled) return;
      const byAnimal: Record<string, string> = {};
      for (const [id, ref] of entries) {
        if (resolved[ref]) byAnimal[id] = resolved[ref];
      }
      setUrls(byAnimal);
    });

    return () => { cancelled = true; };
  }, [key]);

  return urls;
};
